import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PHOTOS } from "@/lib/assets";

const STEPS = [
  { num: "01", title: "Postula", desc: "Cuéntanos qué buscas y qué dudas tienes." },
  { num: "02", title: "Conversemos", desc: "Te explicamos la actividad, los servicios y las condiciones." },
  {
    num: "03",
    title: "Definimos tu proyecto",
    desc: "Acordamos límites, imagen, canales de promoción y alcance.",
  },
  {
    num: "04",
    title: "Formalizamos",
    desc: "Revisamos el contrato y realizas personalmente las verificaciones de identidad exigidas.",
  },
  {
    num: "05",
    title: "Comenzamos",
    desc: "Entregas el material autorizado y nuestro equipo organiza la gestión acordada.",
  },
];

const CREATOR_RESP = [
  "Es mayor de 18 años.",
  "Realiza personalmente su verificación de identidad.",
  "Define y autoriza los usos del contenido.",
  "Entrega material propio o con derechos suficientes.",
  "Conserva acceso a sus propias cuentas y a la información financiera.",
];

const AGENCY_RESP = [
  "Administra la estrategia y el contenido autorizado.",
  "Crea y gestiona cuentas promocionales.",
  "Coordina la operación comercial.",
  "Entrega liquidaciones y gestiona pagos cuando el mecanismo esté permitido.",
];

const CONDITIONS = [
  { label: "Comisión de la agencia", value: "35 % de la base neta." },
  {
    label: "Participación de la creadora",
    value: "65 % de esa base, antes de gastos aprobados a su cargo e impuestos personales.",
  },
  {
    label: "Base neta",
    value: "Ingresos efectivamente cobrados menos cargos de plataforma, devoluciones y contracargos documentados.",
  },
  { label: "Plazo inicial", value: "Dos meses calendario." },
  { label: "Aviso de no renovación", value: "15 días corridos antes del vencimiento." },
  { label: "Renovación", value: "Mensual si no existe aviso oportuno, según contrato." },
  { label: "Gastos adicionales", value: "Únicamente los acordados y aprobados por escrito." },
];

const PRIVACY_OPTIONS = [
  "Utilizar un nombre artístico.",
  "Acordar si se muestra rostro o voz.",
  "Mantener separadas las redes personales.",
  "Definir países a bloquear donde exista esa función.",
  "Autorizar los canales y usos de promoción.",
  "Solicitar retiro de contenido.",
];

export default function ComoFunciona() {
  return (
    <div data-testid="como-funciona-page">
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">
              Cómo funciona
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-4 leading-[1.1] max-w-3xl">
              Un proceso claro, paso a paso.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* PASOS */}
      <section className="pb-20 lg:pb-28" data-testid="section-pasos">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-line border border-line">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.08}>
                <div data-testid={`step-card-${s.num}`} className="bg-ivory p-8 h-full hover:bg-cream transition-colors duration-500">
                  <span className="font-serif text-4xl text-champagne">{s.num}</span>
                  <h3 className="font-serif text-xl text-ink mt-5">{s.title}</h3>
                  <p className="mt-3 text-sm font-light text-stone2 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RESPONSABILIDADES */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="section-responsabilidades">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink leading-[1.15]">
              Responsabilidades de cada parte.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mt-14">
            <Reveal>
              <div data-testid="responsabilidades-creadora" className="bg-ivory border border-line p-8 lg:p-10 h-full">
                <h3 className="font-serif text-2xl text-ink mb-6">La creadora</h3>
                <ul className="space-y-4">
                  {CREATOR_RESP.map((r) => (
                    <li key={r} className="flex gap-3 text-sm font-light text-stone2 leading-relaxed">
                      <Check size={16} className="text-mauve shrink-0 mt-0.5" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="responsabilidades-agencia" className="bg-ivory border border-line p-8 lg:p-10 h-full">
                <h3 className="font-serif text-2xl text-ink mb-6">La agencia</h3>
                <ul className="space-y-4">
                  {AGENCY_RESP.map((r) => (
                    <li key={r} className="flex gap-3 text-sm font-light text-stone2 leading-relaxed">
                      <Check size={16} className="text-mauve shrink-0 mt-0.5" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <p data-testid="nota-cuentas-promocionales" className="bg-ivory border-l-2 border-dustyrose p-6 text-sm font-light text-stone2 leading-relaxed">
                Las cuentas promocionales propias de la agencia quedan bajo su administración exclusiva. La creadora no
                recibe sus credenciales. Esto no incluye sus redes personales preexistentes.
              </p>
              <p data-testid="nota-versiones-editadas" className="bg-ivory border-l-2 border-dustyrose p-6 text-sm font-light text-stone2 leading-relaxed">
                La agencia no está obligada a entregar todas las versiones editadas, descartes o archivos de trabajo.
                Esta condición se explica antes de firmar.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONDICIONES COMERCIALES */}
      <section className="py-20 lg:py-28 bg-plum text-ivory" data-testid="section-condiciones">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-dustyrose font-sans font-semibold">
              Condiciones comerciales
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 leading-[1.15]">
              Una oportunidad con condiciones claras.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="border border-dustyrose/40 p-8 lg:p-10 text-center" data-testid="split-panel">
                  <div className="font-serif text-6xl text-ivory">65<span className="text-2xl align-top">%</span></div>
                  <p className="text-xs uppercase tracking-[0.2em] text-dustyrose mt-2 font-sans">Creadora</p>
                  <div className="w-12 h-px bg-dustyrose/40 mx-auto my-6" />
                  <div className="font-serif text-6xl text-ivory/70">35<span className="text-2xl align-top">%</span></div>
                  <p className="text-xs uppercase tracking-[0.2em] text-dustyrose/70 mt-2 font-sans">Agencia</p>
                  <p className="text-xs font-light text-[#C9B8BE] mt-6">Sobre la base neta definida en contrato.</p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="space-y-0 border-t border-ivory/10">
                {CONDITIONS.map((c, i) => (
                  <Reveal key={c.label} delay={i * 0.05}>
                    <div
                      data-testid={`condition-${i + 1}`}
                      className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-6 py-5 border-b border-ivory/10"
                    >
                      <span className="text-xs uppercase tracking-[0.18em] text-dustyrose font-sans font-semibold pt-1">
                        {c.label}
                      </span>
                      <span className="sm:col-span-2 text-sm font-light text-[#E8DDE2] leading-relaxed">{c.value}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
          <Reveal>
            <div className="mt-12 space-y-4 max-w-4xl">
              <p data-testid="condiciones-pagos-nota" className="text-sm font-light text-[#C9B8BE] leading-relaxed">
                Los ingresos pueden recibirse por la agencia o el integrante identificado como tercero autorizado, para
                posteriormente liquidar y pagar a la creadora, siempre que la plataforma, el banco y el proveedor
                acepten el mecanismo. Si no lo permiten, se acuerda una alternativa compatible.
              </p>
              <p data-testid="condiciones-consent-nota" className="text-sm font-light text-[#C9B8BE] leading-relaxed">
                El compromiso comercial no obliga a producir contenido íntimo ni impide retirar consentimiento. Las
                condiciones completas y las causas de término se revisan antes de contratar.
              </p>
              <p data-testid="condiciones-contrato-nota" className="text-xs font-light text-[#9E8B93] leading-relaxed">
                Este resumen es informativo y no reemplaza el contrato, que se encuentra sujeto a revisión antes de su
                firma.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRIVACIDAD Y EXPOSICIÓN */}
      <section className="py-20 lg:py-28" data-testid="section-privacidad-exposicion">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">
                Privacidad y exposición
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mt-4 leading-[1.15]">
                Tu imagen, con límites definidos.
              </h2>
              <ul className="mt-10 space-y-4">
                {PRIVACY_OPTIONS.map((p) => (
                  <li key={p} className="flex gap-3 text-base font-light text-stone2 leading-relaxed">
                    <Check size={17} className="text-mauve shrink-0 mt-1" />
                    {p}
                  </li>
                ))}
              </ul>
              <p
                data-testid="privacidad-aclaracion"
                className="mt-10 border-l-2 border-champagne pl-4 text-sm text-stone2 leading-relaxed max-w-lg"
              >
                Un seudónimo o bloqueo geográfico no garantiza anonimato ni impide capturas o redistribución.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.12}>
              <img
                src={PHOTOS.studio}
                alt="Reunión de trabajo en estudio creativo"
                data-testid="privacidad-image"
                className="w-full h-[380px] sm:h-[480px] object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink leading-[1.15]">
              ¿Conversemos sobre tu proyecto?
            </h2>
            <Link
              to="/postula"
              data-testid="como-funciona-cta-postula"
              className="mt-10 inline-flex items-center gap-2 bg-plum text-ivory hover:bg-mauve px-10 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 font-sans"
            >
              Solicitar una reunión <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
